import type {ISmsClientsFilter, ISmartNudgeSuggestion, ISmartNudgeSendRequest, IAutomationConfig, AutomationTypeEnum} from "@torim-app/types";
import {ISmsAutomation, ISmsFilterStats} from "@torim-app/types";
import {adminUrlBuilder} from "@app/api/utils";

export class CommunicationApi {
  static path = 'communication';

  static async fetchFilterEstimatedClients(
    smsClientsFilter: ISmsClientsFilter
  ): Promise<ISmsFilterStats> {
    const {arg, dateRange, customersBase, condition} = smsClientsFilter;
    const params: Record<string, any> = {
      'customers-base': customersBase,
      'start-date': dateRange?.start ?? undefined,
      'end-date': dateRange?.end ?? undefined,
      condition, arg
    };

    const url = adminUrlBuilder(params, `${CommunicationApi.path}/bulk-sms`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (response.status === 404)
      throw "Failed to get estimated clients for the sms filter";

    return await response.json();
  }

  static async sendBulkSMSbyFilter(
    filter: ISmsClientsFilter,
    message: string,
  ): Promise<void> {
    const body: { message: string, filter: ISmsClientsFilter } = {
      message, filter
    }
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/bulk-sms`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (response.status > 400)
      throw "Failed to send bulk sms with filter";
  }

  static async fetchAutomations(): Promise<Array<ISmsAutomation>> {
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/automations`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (response.status === 404)
      throw "Failed to get automations";

    return await response.json();
  }

  static async removeAutomations(
    automationId: string
  ): Promise<void> {
    const url = adminUrlBuilder({aid: automationId}, `${CommunicationApi.path}/automations`);
    const response = await fetch(url, {
      method: 'delete',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (response.status === 404)
      throw "Failed to remove automations";
  }

  static async saveAutomation(
    body: ISmsAutomation
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/automations`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (response.status > 400)
      throw "Failed to save automations";
  }

  static async fetchSmartNudgeSuggestions(): Promise<ISmartNudgeSuggestion[]> {
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/smart-nudge`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (response.status === 404)
      throw "Failed to get smart nudge suggestions";

    return await response.json();
  }

  static async sendSmartNudge(
    request: ISmartNudgeSendRequest
  ): Promise<{ success: boolean }> {
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/smart-nudge/send`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(request)
    });
    if (response.status > 400)
      throw "Failed to send smart nudge";

    return await response.json();
  }

  static async dismissSmartNudge(
    suggestionId: string
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/smart-nudge/dismiss/${suggestionId}`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (response.status > 400)
      throw "Failed to dismiss smart nudge";
  }

  static async regenerateSmartNudge(): Promise<{ count: number }> {
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/smart-nudge/regenerate`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (response.status > 400)
      throw "Failed to regenerate smart nudge suggestions";

    return await response.json();
  }

  /* ---------- Automation Configs (v2) ---------- */

  static async fetchAutomationConfigs(): Promise<IAutomationConfig[]> {
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/automation-configs`);
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' }
    });
    if (response.status === 404)
      throw "Failed to get automation configs";
    return await response.json();
  }

  static async upsertAutomationConfig(config: IAutomationConfig): Promise<IAutomationConfig> {
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/automation-configs`);
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(config)
    });
    if (response.status > 400)
      throw "Failed to save automation config";
    return await response.json();
  }

  static async deleteAutomationConfig(type: AutomationTypeEnum): Promise<void> {
    const url = adminUrlBuilder({}, `${CommunicationApi.path}/automation-configs/${type}`);
    const response = await fetch(url, {
      method: 'delete',
      headers: { 'content-type': 'application/json' }
    });
    if (response.status > 400)
      throw "Failed to delete automation config";
  }
}
